import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SURVEY_CATEGORIES, SurveyCategory } from '../../types';
import { useTheme } from '../../theme';
import { AnimatedIcon } from '../../components/icons';

export const ClientCategories: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [hoveredService, setHoveredService] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(searchParams.get('cat'));
  const { isDark } = useTheme();

  const handleRequestService = (serviceType: string) => {
    navigate(`/client/request/create?serviceType=${encodeURIComponent(serviceType)}`);
  };

  const shown = activeCategory
    ? SURVEY_CATEGORIES.filter((c) => c.id === activeCategory)
    : SURVEY_CATEGORIES;

  return (
    <div>
      {/* Page Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.pageTitle}>Survey Categories</h1>
          <p style={styles.pageSubtitle}>
            Browse available professional surveying services. Select a service to submit a project request.
          </p>
        </div>
        <div style={styles.statsRow}>
          <div style={styles.statPill}>
            <span style={styles.statNum}>{SURVEY_CATEGORIES.length}</span>
            <span style={styles.statLabel}>Categories</span>
          </div>
          <div style={styles.statPill}>
            <span style={{ ...styles.statNum, color: 'var(--sc-brand-primary)' }}>
              {SURVEY_CATEGORIES.reduce((s, c) => s + c.services.length, 0)}
            </span>
            <span style={styles.statLabel}>Services</span>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div style={styles.filterRow}>
        <button
          style={{
            ...styles.filterTab,
            background: activeCategory === null ? 'var(--sc-brand-primary-bg)' : 'var(--sc-bg-secondary)',
            borderColor: activeCategory === null ? 'var(--sc-brand-primary)' : 'var(--sc-border)',
            color: activeCategory === null ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)',
            fontWeight: activeCategory === null ? 700 : 500,
          }}
          onClick={() => setActiveCategory(null)}
          className="sc-press"
        >
          All Categories
        </button>
        {SURVEY_CATEGORIES.map((cat) => {
          const active = activeCategory === cat.id;
          return (
            <button
              key={cat.id}
              style={{
                ...styles.filterTab,
                background: active ? 'var(--sc-bg-active)' : 'var(--sc-bg-secondary)',
                borderColor: active ? cat.color : 'var(--sc-border)',
                color: active ? cat.color : 'var(--sc-text-secondary)',
                fontWeight: active ? 700 : 500,
              }}
              onClick={() => setActiveCategory(active ? null : cat.id)}
              className="sc-press"
            >
              <span style={{ marginRight: 6 }}>{cat.icon}</span> 
              <span>{cat.name.split(' ')[0]}</span>
            </button>
          );
        })}
      </div>

      {/* Category Cards Grid */}
      <div style={styles.grid}>
        {shown.map((cat) => {
          const isHov = hoveredCard === cat.id;
          return (
            <div
              key={cat.id}
              style={{
                ...styles.card,
                boxShadow: isHov
                  ? `0 12px 32px -8px ${cat.color}25, var(--sc-shadow-lg)`
                  : 'var(--sc-shadow-md)',
                borderTop: `3px solid ${cat.color}`,
                transform: isHov ? 'translateY(-2px)' : 'none',
              }}
              onMouseEnter={() => setHoveredCard(cat.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* Card Header */}
              <div style={styles.cardHeader}>
                <div style={styles.catIcon}>
                  <span style={{ fontSize: 26 }}>{cat.icon}</span>
                </div>
                <div style={styles.catMeta}>
                  <h2 style={styles.catName}>{cat.name}</h2>
                  <span style={{ ...styles.categoryBadge, background: cat.color + '18', color: cat.color }}>
                    {cat.services.length} services
                  </span>
                </div>
              </div>

              {/* Description */}
              <p style={styles.catDesc}>{cat.description}</p>

              {/* Services Grid */}
              <div style={styles.servicesLabel}>Available Services</div>
              <div style={styles.servicesGrid}>
                {cat.services.map((service) => {
                  const isServHov = hoveredService === service;
                  return (
                    <button
                      key={service}
                      style={{
                        ...styles.serviceChip,
                        background: isServHov ? cat.color : 'var(--sc-bg-input)',
                        color: isServHov ? '#FFF' : 'var(--sc-text-primary)',
                        borderColor: isServHov ? cat.color : 'var(--sc-border)',
                        boxShadow: isServHov ? `0 4px 12px ${cat.color}35` : 'none',
                        transform: isServHov ? 'scale(1.02)' : 'none',
                      }}
                      onMouseEnter={() => setHoveredService(service)}
                      onMouseLeave={() => setHoveredService(null)}
                      onClick={() => handleRequestService(service)}
                      title={`Request: ${service}`}
                    >
                      {service}
                    </button>
                  );
                })}
              </div>

              {/* Card Footer */}
              <div style={styles.cardFooter}>
                <button
                  style={{ ...styles.cardCtaBtn, background: cat.color }}
                  onClick={() => handleRequestService(cat.services[0])}
                  className="sc-press"
                >
                  <AnimatedIcon name="pen" size={14} color="#FFF" />
                  <span style={{ marginLeft: 6 }}>Request {cat.name.split(' ')[0]} Survey</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recommendation Banner */}
      <div style={styles.infoBanner} className="sc-animate-scale-in">
        <div style={styles.infoBannerIcon}>
          <AnimatedIcon name="bell" size={24} color="var(--sc-success-text)" />
        </div>
        <div style={{ flex: 1, minWidth: 260 }}>
          <strong style={{ color: 'var(--sc-success-text)', fontSize: 14 }}>Not sure which service fits your project?</strong>
          <p style={{ margin: '4px 0 0', color: 'var(--sc-text-secondary)', fontSize: 13, lineHeight: 1.5 }}>
            Submit a general project request. Our engineering team will clarify requirements and match you with a certified Surveyor.
          </p>
        </div>
        <button
          style={styles.infoCta}
          onClick={() => navigate('/client/request/create')}
          className="sc-press"
        >
          Submit General Request
        </button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    flexWrap: 'wrap',
    gap: 16,
  },
  pageTitle: { fontSize: 28, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', letterSpacing: '-0.5px', fontFamily: "var(--sc-font-display)" },
  pageSubtitle: { fontSize: 14, color: 'var(--sc-text-secondary)', margin: 0, maxWidth: 540 },

  statsRow: { display: 'flex', gap: 12 },
  statPill: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: 'var(--sc-bg-secondary)',
    borderRadius: 'var(--sc-radius-md)',
    padding: '10px 18px',
    border: '1px solid var(--sc-border)',
    boxShadow: 'var(--sc-shadow-sm)',
    minWidth: 80,
  },
  statNum: { fontSize: 22, fontWeight: 800, color: 'var(--sc-success)' },
  statLabel: { fontSize: 11, color: 'var(--sc-text-tertiary)', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 },

  filterRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 24,
    overflowX: 'auto',
    paddingBottom: 4,
    scrollbarWidth: 'none',
  },
  filterTab: {
    padding: '8px 16px',
    borderRadius: 20,
    border: '1px solid var(--sc-border)',
    fontSize: 12.5,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all var(--sc-transition-fast)',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
  },

  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: 20,
    marginBottom: 28,
  },

  card: {
    background: 'var(--sc-bg-secondary)',
    borderRadius: 'var(--sc-radius-lg)',
    padding: 24,
    border: '1px solid var(--sc-border)',
    transition: 'all var(--sc-transition-base)',
    display: 'flex',
    flexDirection: 'column',
  },

  cardHeader: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 },
  catIcon: {
    width: 52,
    height: 52,
    borderRadius: '50%',
    background: 'var(--sc-bg-input)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 24,
    flexShrink: 0,
    boxShadow: 'var(--sc-shadow-xs)',
  },
  catMeta: { flex: 1 },
  catName: { fontSize: 16, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '0 0 4px', fontFamily: 'var(--sc-font-display)' },
  categoryBadge: {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
  },

  catDesc: {
    fontSize: 13,
    color: 'var(--sc-text-secondary)',
    lineHeight: 1.5,
    margin: '0 0 16px',
  },

  servicesLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: 'var(--sc-text-tertiary)',
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  servicesGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 20,
    flex: 1,
  },
  serviceChip: {
    padding: '6px 12px',
    borderRadius: 'var(--sc-radius-sm)',
    border: '1px solid var(--sc-border)',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all var(--sc-transition-fast)',
  },

  cardFooter: { borderTop: '1px solid var(--sc-border-light)', paddingTop: 16 },
  cardCtaBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: '10px 16px',
    borderRadius: 'var(--sc-radius-md)',
    border: 'none',
    color: '#FFF',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    boxShadow: 'var(--sc-shadow-sm)',
  },

  infoBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    background: 'var(--sc-success-bg)',
    border: '1px solid var(--sc-success)',
    borderRadius: 'var(--sc-radius-lg)',
    padding: '16px 20px',
    flexWrap: 'wrap',
    marginTop: 20,
  },
  infoBannerIcon: { fontSize: 24, flexShrink: 0 },
  infoCta: {
    marginLeft: 'auto',
    padding: '10px 18px',
    background: 'var(--sc-success)',
    color: '#FFF',
    border: 'none',
    borderRadius: 'var(--sc-radius-md)',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    boxShadow: 'var(--sc-shadow-sm)',
  },
};
